import React, {useEffect, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import Icon from '@/components/icons/Icon';
import Spinner from '@/components/ui/Spinner';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';

const ThinkingSpinner: React.FC<{progress?: number; status?: string}> = ({progress, status}) => {
    const [showThinkingText, setShowThinkingText] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowThinkingText(true);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);


    return (
        <div className="cb-thinking-spinner">
            <Spinner
                variant="dots"
                size="sm"
                text={status || (showThinkingText ? i18n.t('jt_thinking.text') : undefined)}
            />
            {progress !== undefined && (
                <div className="cb-progress-container" style={{marginTop: '8px'}}>
                    <div className="cb-progress-bar" style={{
                        width: '100%',
                        height: '4px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '2px',
                        overflow: 'hidden'
                    }}>
                        <div
                            className="cb-progress-fill"
                            style={{
                                width: `${progress}%`,
                                height: '100%',
                                backgroundColor: '#009efb',
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </div>
                    <div className="cb-progress-text" style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        marginTop: '4px',
                        textAlign: 'center'
                    }}>
                        {Math.round(progress)}%
                    </div>
                </div>
            )}
        </div>
    );
};

const formatMessageTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

interface Message {
    sender: 'user' | 'bot';
    text: string;
    timestamp: Date;
    isStreaming?: boolean;
    progress?: number;
    status?: string;
}

interface ChatInterfaceProps {
    messages: Message[];
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    conversationHash?: string | null;
}

const getOpeningMessage = () => {
    if (window.location.pathname.includes('/events/event')) {
        return window.FCONST.FS_ASKAI_OPENING_MESSAGE_EVENT_PAGE || 'How can I help you with this event?';
    }
    else if (window.location.pathname.includes('/knowledge-base')) {
        return window.FCONST.FS_ASKAI_OPENING_MESSAGE_KNOWLEDGE_BASE_LINKS || 'How can I help you with this topic?';
    }
    return window.FCONST.FS_ASKAI_OPENING_MESSAGE_GENERAL || 'How can I help you today?';
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
    messages,
    setMessages,
    conversationHash
}) => {
    const [openingMessage] = useState(getOpeningMessage());
    const [openingTimestamp] = useState(new Date());
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [streamingProgress, setStreamingProgress] = useState<number>(0);
    const [streamingStatus, setStreamingStatus] = useState<string>('');
    const [currentEventSource, setCurrentEventSource] = useState<EventSource | null>(null);
    const [currentAbortController, setCurrentAbortController] = useState<AbortController | null>(null);
    const [streamingMessage, setStreamingMessage] = useState<string>('');
    const [isStreamingContent, setIsStreamingContent] = useState<boolean>(false);
    const chatBoxRef = useRef<HTMLDivElement>(null);

    // Auto-send message state
    const [autoSendMessage, setAutoSendMessage] = useState<string | null>(null);

    // Listen for auto-send message events (e.g., from chart analysis)
    useEffect(() => {
        const handleAutoSendMessage = (event: CustomEvent<{message: string}>) => {
            const {message} = event.detail;
            setAutoSendMessage(message);
        };

        window.addEventListener('autoSendMessage', handleAutoSendMessage as EventListener);

        return () => {
            window.removeEventListener('autoSendMessage', handleAutoSendMessage as EventListener);
        };
    }, []);

    // Auto-send message when autoSendMessage is set
    useEffect(() => {
        if (autoSendMessage && !isThinking) {
            setInput(autoSendMessage);
            setAutoSendMessage(null);

            // Trigger send after setting input
            setTimeout(() => {
                // Simulate pressing Enter key to trigger send
                const inputElement = document.getElementById('text-input') as HTMLInputElement;
                if (inputElement) {
                    const enterEvent = new KeyboardEvent('keydown', {
                        key: 'Enter',
                        code: 'Enter',
                        keyCode: 13,
                        which: 13,
                        bubbles: true
                    });
                    inputElement.dispatchEvent(enterEvent);
                }
            }, 100);
        }
    }, [autoSendMessage, isThinking]);

    const processStreamingMessage = (data: any, startTime: number) => {
        const messageTime = Date.now() - startTime;

        switch (data.type) {
            case 'status':
                flushSync(() => {
                    setStreamingProgress(data.progress || 0);
                    setStreamingStatus(data.message || 'Processing...');
                });
                break;

            case 'content_chunk':
                const wordCount = (data.content || '').split(' ').length;

                flushSync(() => {
                    setIsStreamingContent(true);
                    setStreamingMessage(data.content || '');
                    setStreamingProgress(data.progress || 80);
                    setStreamingStatus(`Streaming... ${wordCount} words`);
                    setIsThinking(true);
                });
                break;

            case 'result':
                const responseContent = data.data?.content || data.data?.result || 'No response received';

                setMessages(prev => [...prev, {
                    id: Date.now(),
                    text: responseContent,
                    sender: 'bot',
                    timestamp: new Date()
                }]);

                setIsThinking(false);
                setStreamingProgress(0);
                setStreamingStatus('');
                setIsStreamingContent(false);
                setStreamingMessage('');
                break;

            case 'complete':
                setIsThinking(false);
                setStreamingProgress(0);
                setStreamingStatus('');
                setIsStreamingContent(false);
                setStreamingMessage('');
                break;

            case 'error':
                setIsThinking(false);
                setStreamingProgress(0);
                setStreamingStatus('');
                setIsStreamingContent(false);
                setStreamingMessage('');

                setMessages(prev => [...prev, {
                    id: Date.now(),
                    text: `Error: ${data.error || 'Unknown error occurred'}`,
                    sender: 'bot',
                    timestamp: new Date()
                }]);
                break;

            default:
            // Unknown message type
        }
    };

    const sendMessage = async () => {
        if (!input.trim() || isThinking) {return;}

        const startTime = Date.now();

        const userMsg: Message = {sender: 'user', text: input, timestamp: new Date()};
        setMessages(msgs => [...msgs, userMsg]);
        const userInput = input;
        setInput('');
        setIsThinking(true);
        setStreamingProgress(0);
        setStreamingStatus('Connecting...');
        setStreamingMessage('');
        setIsStreamingContent(false);


        // Close any existing EventSource
        if (currentEventSource) {
            currentEventSource.close();
        }

        const params = new URLSearchParams({
            msg: userInput,
            page: window.location.pathname,
            path: window.location.href,
            newChat: (!conversationHash && messages.length === 0) ? 'true' : 'false',
            ...(conversationHash && {conversationHash: conversationHash})
        });

        // Try streaming first, fallback to regular request if streaming fails
        // Use chunked streaming instead of SSE for better authentication support
        const streamingUrl = `${buildURL('askaiChunkedAgent')}?${params.toString()}`;
        const fallbackUrl = `${buildURL('askaiAgent')}?${params.toString()}`;

        try {
            // Starting fetch-based streaming

            // Create AbortController for cancellation
            const abortController = new AbortController();
            setCurrentAbortController(abortController);

            const response = await fetch(streamingUrl, {
                method: 'GET',
                credentials: 'include', // Include cookies for authentication
                headers: {
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache'
                },
                signal: abortController.signal
            });


            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';


            try {
                while (true) {
                    const {done, value} = await reader.read();

                    if (done) {
                        // Clean up abort controller
                        setCurrentAbortController(null);
                        break;
                    }

                    // Decode chunk and add to buffer
                    const chunk = decoder.decode(value, {stream: true});
                    buffer += chunk;

                    // Process complete JSON objects
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep incomplete line in buffer

                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                const data = JSON.parse(line);

                                // Process the message (same logic as EventSource)
                                processStreamingMessage(data, startTime);

                            } catch (parseError) {
                                console.error('❌ [FETCH STREAMING] JSON parse error:', parseError, 'Line:', line);
                            }
                        }
                    }
                }
            } finally {
                reader.releaseLock();
            }

            // Fetch streaming completed successfully

        } catch (error: any) {
            // Clean up abort controller
            setCurrentAbortController(null);

            // Check if this was a user cancellation
            if (error?.name === 'AbortError') {
                // User cancelled - don't show error, cancellation message already shown
                return;
            }

            // Immediate fallback if EventSource creation fails
            try {
                const response = await fetch(fallbackUrl);
                const data = await response.json();
                const responseContent = data.content || data.result || 'No response received';

                setMessages(msgs => [...msgs, {
                    sender: 'bot',
                    text: responseContent,
                    timestamp: new Date()
                }]);
            } catch (fallbackError) {
                setMessages(msgs => [...msgs, {
                    sender: 'bot',
                    text: 'Sorry, there was an error. Please try again.',
                    timestamp: new Date()
                }]);
            } finally {
                setIsThinking(false);
                setStreamingProgress(0);
                setStreamingStatus('');
            }
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !isThinking) {
            sendMessage();
        }
    };

    const cancelStreaming = () => {
        // Cancel fetch-based streaming
        if (currentAbortController) {
            currentAbortController.abort();
            setCurrentAbortController(null);
        }

        // Cancel EventSource (fallback)
        if (currentEventSource) {
            currentEventSource.close();
            setCurrentEventSource(null);
        }

        // Reset streaming state
        setIsThinking(false);
        setStreamingProgress(0);
        setStreamingStatus('');
        setIsStreamingContent(false);
        setStreamingMessage('');

        // Add cancelled message
        setMessages(prev => [...prev, {
            id: Date.now(),
            text: 'Response cancelled by user.',
            sender: 'bot',
            timestamp: new Date()
        }]);
    };

    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
    }, [messages]);

    // Debug streaming state changes (DISABLED to prevent infinite loop)
    // useEffect(() => {
    // }, [isStreamingContent, streamingMessage]);

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            if (currentEventSource) {
                currentEventSource.close();
            }
            if (currentAbortController) {
                currentAbortController.abort();
            }
        };
    }, [currentEventSource, currentAbortController]);

    return (
        <>
            <div className="cb-chat-box" id="chat-box" ref={chatBoxRef}>
                <div className="cb-message-wrapper cb-bot">
                    <div className="cb-message">
                        <span id="opening-message" dangerouslySetInnerHTML={{__html: openingMessage}} />
                        <div className="cb-message-timestamp cb-message-timestamp-bot">
                            {formatMessageTime(openingTimestamp)}
                        </div>
                    </div>
                </div>
                {messages.map((msg, idx) => (
                    <div key={idx} className={`cb-message-wrapper cb-${msg.sender}`}>
                        <div className="cb-message">
                            {msg.sender === 'bot' ? (
                                <span dangerouslySetInnerHTML={{__html: msg.text}} />
                            ) : (
                                <span>{msg.text}</span>
                            )}
                            <div className={`cb-message-timestamp cb-message-timestamp-${msg.sender} cb-message-timestamp-hover`}>
                                {formatMessageTime(msg.timestamp)}
                            </div>
                        </div>
                    </div>
                ))}
                {isThinking && !isStreamingContent && (
                    <div className="cb-message-wrapper cb-bot">
                        <div className="cb-message">
                            <ThinkingSpinner
                                progress={streamingProgress}
                                status={streamingStatus}
                            />
                        </div>
                    </div>
                )}
                {isStreamingContent && (
                    <div key={`streaming-${streamingMessage.length}`} className="cb-message-wrapper cb-bot">
                        <div className="cb-message">
                            {streamingMessage}
                            <span className="cb-streaming-cursor">|</span>
                            <div style={{marginTop: '8px'}}>
                                <button
                                    onClick={cancelStreaming}
                                    style={{
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                    onMouseOver={(e) => (e.target as HTMLElement).style.backgroundColor = '#c82333'}
                                    onMouseOut={(e) => (e.target as HTMLElement).style.backgroundColor = '#dc3545'}
                                >
                                    ⏹ Stop
                                </button>
                            </div>
                            <div className="cb-progress-container" style={{marginTop: '8px'}}>
                                <div className="cb-progress-bar" style={{
                                    width: '100%',
                                    height: '4px',
                                    backgroundColor: '#e9ecef',
                                    borderRadius: '2px',
                                    overflow: 'hidden'
                                }}>
                                    <div
                                        className="cb-progress-fill"
                                        style={{
                                            width: `${streamingProgress}%`,
                                            height: '100%',
                                            backgroundColor: '#28a745',
                                            transition: 'width 0.3s ease'
                                        }}
                                    />
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#6c757d',
                                    marginTop: '4px',
                                    textAlign: 'center'
                                }}>
                                    {Math.round(streamingProgress)}% - {streamingStatus}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="cb-chat-footer">
                <div className="cb-chat-footer-container">
                    <input
                        id="text-input"
                        type="text"
                        name="msg"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        placeholder="Ask a question"
                        autoComplete="off"
                    />
                    {isThinking ? (
                        <button className="cb-send-btn cb-cancel-btn" onClick={cancelStreaming} title={i18n.t('jt_cancel.text')}>
                            <Icon name="mdi-close" size="1x" />
                        </button>
                    ) : (
                        <button className="cb-send-btn" onClick={sendMessage} data-cy="chat-interface-btn" disabled={!input.trim()}>
                            <Icon name="mdi-send" size="1x" />
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default ChatInterface;
